import { useEffect, useMemo, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { LogOut, ArrowUpRight, PanelLeftClose, PanelLeftOpen, Menu, X, Search, Command, FlaskConical } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useTenant } from '../contexts/TenantContext'
import { STAFF_NAV, ALL_NAV_ITEMS, findNavItem } from '../config/staffNav'
import { useAppEnv } from '../hooks/useAppEnv'
import CommandPalette from './CommandPalette'
import '../styles/public.css'
import '../styles/dashboard.css'

const COLLAPSE_KEY = 'dash.sidebarCollapsed'

export default function Layout({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, canAccessPage } = useAuth()
  const tenant = useTenant()
  const env = useAppEnv()

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1' } catch { return false }
  })
  const [mobileOpen, setMobileOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)

  useEffect(() => { try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0') } catch { /* ignore */ } }, [collapsed])
  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  // Ctrl/Cmd+K opens the page switcher.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPaletteOpen((o) => !o) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const sections = useMemo(() => STAFF_NAV
    .map((s) => ({ ...s, items: s.items.filter((i) => (!i.feature || tenant.features?.[i.feature]) && canAccessPage(i.path)) }))
    .filter((s) => s.items.length), [tenant, canAccessPage])

  const current = findNavItem(location.pathname)
  useEffect(() => {
    document.title = (current ? current.label + ' - ' : '') + 'MWRP Staff'
  }, [current])

  const sidebar = (isMobile) => (
    <div className="h-full flex flex-col">
      <div className={'h-[68px] flex items-center shrink-0 ' + (collapsed && !isMobile ? 'justify-center px-2' : 'px-5 justify-between')}>
        {(!collapsed || isMobile) && (
          <Link to="/staff" className="flex items-center gap-2.5 min-w-0">
            <img src={tenant.branding.headerLogo} alt={tenant.branding.brandName} className="h-7 w-auto" />
            <span className="text-[11px] font-semibold text-[#8b8d94] px-2 py-0.5 rounded-full bg-white/[0.06]">Staff</span>
          </Link>
        )}
        {isMobile ? (
          <button onClick={() => setMobileOpen(false)} className="dash-icon-btn" aria-label="Close menu"><X /></button>
        ) : (
          <button onClick={() => setCollapsed((c) => !c)} className="dash-icon-btn" aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
          </button>
        )}
      </div>

      <div className={'shrink-0 ' + (collapsed && !isMobile ? 'px-2' : 'px-3')}>
        <button
          onClick={() => setPaletteOpen(true)}
          className={'w-full h-10 rounded-xl bg-white/[0.04] hover:bg-white/[0.07] border border-white/[0.06] flex items-center text-[13px] text-[#8b8d94] transition-colors ' + (collapsed && !isMobile ? 'justify-center' : 'gap-2.5 px-3')}
          title="Jump to a page (Ctrl+K)"
        >
          <Search className="w-4 h-4" />
          {(!collapsed || isMobile) && <><span className="flex-1 text-left">Jump to...</span><kbd className="hidden sm:inline-flex items-center gap-0.5 text-[10px] text-[#5d6068] font-sans"><Command className="w-3 h-3" />K</kbd></>}
        </button>
      </div>

      <nav className={'flex-1 overflow-y-auto dash-scroll py-4 ' + (collapsed && !isMobile ? 'px-2' : 'px-[18px]')} aria-label="Dashboard">
        {sections.map((section, idx) => (
          <div key={section.title} className={idx ? 'mt-5' : ''}>
            {collapsed && !isMobile ? (idx > 0 && <div className="mx-2 mb-3 border-t border-white/[0.06]" />) : (
              <p className="px-3 mb-1.5 text-[11px] font-medium text-[#5d6068]">{section.title}</p>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const Icon = item.icon
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/staff'}
                    title={collapsed && !isMobile ? item.label : undefined}
                    className={({ isActive }) => 'dash-nav-item ' + (isActive ? 'is-active ' : '') + (collapsed && !isMobile ? 'justify-center !px-0' : '')}
                  >
                    <Icon />
                    {(!collapsed || isMobile) && <span className="truncate">{item.label}</span>}
                  </NavLink>
                )
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className={'shrink-0 p-3 border-t border-white/[0.06] ' + (collapsed && !isMobile ? 'flex flex-col items-center gap-2' : '')}>
        {user && (
          <div className={'flex items-center gap-3 ' + (collapsed && !isMobile ? '' : 'p-2 rounded-2xl bg-white/[0.03]')}>
            <Avatar user={user} />
            {(!collapsed || isMobile) && (
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-white truncate">{user.nickname || user.username}</p>
                <p className="text-[11.5px] text-[#8b8d94] truncate">{user.rank?.name || 'Staff'}</p>
              </div>
            )}
            {(!collapsed || isMobile) && (
              <button onClick={logout} className="dash-icon-btn hover:!text-[#ff6b7d]" aria-label="Sign out" title="Sign out"><LogOut /></button>
            )}
          </div>
        )}
        {collapsed && !isMobile && <button onClick={logout} className="dash-icon-btn hover:!text-[#ff6b7d]" aria-label="Sign out" title="Sign out"><LogOut /></button>}
        {(!collapsed || isMobile) && (
          <Link to="/" className="mt-2 flex items-center justify-center gap-1.5 h-9 rounded-xl text-[12.5px] text-[#8b8d94] hover:text-white hover:bg-white/[0.04] transition-colors">
            View public site <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
    </div>
  )

  return (
    <div className="dash min-h-screen flex">
      {/* Desktop sidebar */}
      <aside className={'hidden lg:block fixed inset-y-0 left-0 z-30 border-r border-white/[0.06] bg-[#0b0c10] transition-[width] duration-300 ease-[cubic-bezier(.2,.7,.2,1)] ' + (collapsed ? 'w-[76px]' : 'w-[264px]')}>
        {sidebar(false)}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm dash-fade" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-[86%] max-w-[300px] bg-[#0b0c10] border-r border-white/[0.06] dash-enter">{sidebar(true)}</aside>
        </div>
      )}

      <div className={'flex-1 min-w-0 transition-[padding] duration-300 ' + (collapsed ? 'lg:pl-[76px]' : 'lg:pl-[264px]')}>
        {env.readOnly && (
          <div className="sticky top-0 z-20 bg-[#f5b20b] text-black">
            <div className="px-4 sm:px-8 py-2 flex items-center gap-2 text-[12.5px] font-medium">
              <FlaskConical className="w-4 h-4 shrink-0" />
              <span className="truncate"><b>Staging preview</b><span className="hidden sm:inline"> - real data, but saving is disabled. Nothing here changes the live server.</span><span className="sm:hidden"> - read-only</span></span>
            </div>
          </div>
        )}

        {/* Mobile top bar */}
        <header className={'lg:hidden sticky z-20 h-14 px-3 flex items-center gap-2 dash-glass !border-x-0 !border-t-0 ' + (env.readOnly ? 'top-[37px]' : 'top-0')}>
          <button onClick={() => setMobileOpen(true)} className="dash-icon-btn" aria-label="Open menu"><Menu /></button>
          <p className="flex-1 min-w-0 truncate text-[14px] font-medium text-white">{current?.label || 'Staff'}</p>
          <button onClick={() => setPaletteOpen(true)} className="dash-icon-btn" aria-label="Search pages"><Search /></button>
        </header>

        <main className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1500px] mx-auto">
          <div key={location.pathname} className="dash-enter">{children}</div>
        </main>
      </div>

      {paletteOpen && (
        <CommandPalette
          items={ALL_NAV_ITEMS.filter((i) => (!i.feature || tenant.features?.[i.feature] || i.feature === 'resourceSwiper') && canAccessPage(i.path))}
          onClose={() => setPaletteOpen(false)}
          onSelect={(path) => { setPaletteOpen(false); navigate(path) }}
        />
      )}
    </div>
  )
}

function Avatar({ user }) {
  const [broken, setBroken] = useState(false)
  if (user.avatar && !broken) return <img src={user.avatar} alt="" onError={() => setBroken(true)} className="w-9 h-9 rounded-full shrink-0 ring-1 ring-white/10" />
  return (
    <span className="w-9 h-9 rounded-full shrink-0 bg-white/10 flex items-center justify-center text-[13px] font-semibold text-white">
      {(user.nickname || user.username || '?').charAt(0).toUpperCase()}
    </span>
  )
}
